import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FolderKanban, FileCheck2, Clock, AlertTriangle, Star, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { isPastDue, parseDateOnly } from '@/lib/planPackage';
import { Link } from 'react-router-dom';
import StatCard from '../components/dashboard/StatCard';
import ProjectCard from '../components/dashboard/ProjectCard';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import ProjectList from '../components/dashboard/ProjectList';
import ProjectViewToggle from '../components/dashboard/ProjectViewToggle';
import ProjectTypeFilter from '../components/dashboard/ProjectTypeFilter';
import PortfolioFilter from '../components/dashboard/PortfolioFilter';
import { useProjectViewMode, VIEW_LIST } from '@/hooks/useProjectViewMode';
import { filterProjectsByType, TYPE_FILTER_ALL } from '@/lib/projectFilters';
import {
  filterProjectsByPortfolio, portfolioByPropertyName, portfolioLabel, PORTFOLIO_FILTER_ALL,
} from '@/lib/propertyPortfolio';
import { TYPE_LABELS } from '@/lib/projectConstants';
import { INACTIVE_STATUSES } from '@/lib/projectStatus';
import {
  activitiesQuery, milestonesQuery, plansQuery, projectsQuery, propertiesQuery, tasksQuery, usersQuery,
} from '@/lib/portfolioQueries';
import { withPmNames } from '@/lib/propertyManagerGroups';
import { awaitingRevisions, waitingLabel } from '@/lib/awaitingRevisions';

function calcProjectPercent(milestones, tasks) {
  const total = milestones.length + tasks.length;
  if (!total) return 0;
  const done = milestones.filter(m => m.status === 'complete').length + tasks.filter(t => t.status === 'complete').length;
  return Math.round((done / total) * 100);
}

export default function Dashboard() {
  const [viewMode, setViewMode] = useProjectViewMode();
  /* Deliberately not shared with the Projects page, unlike the tiles/list
     preference above. The view mode is how someone likes to read a list; the
     type filter is what they are looking at right now, and carrying it across
     pages would mean arriving at Projects to find most of them missing with
     the reason a page away. */
  const [typeFilter, setTypeFilter] = useState(TYPE_FILTER_ALL);
  const [portfolioFilter, setPortfolioFilter] = useState(PORTFOLIO_FILTER_ALL);
  /* The API already returns only the projects this user may see, so there is
     no filter here. The one that used to sit here looked only at `team` and
     hid the projects people were assigned to. */
  const { data: projects = [] } = useQuery(projectsQuery);
  const { data: plans = [] } = useQuery(plansQuery);
  const { data: milestones = [] } = useQuery(milestonesQuery);
  const { data: tasks = [] } = useQuery(tasksQuery);
  const { data: activities = [] } = useQuery(activitiesQuery);
  /* For the portfolio filter: a project's portfolio is its property's. */
  const { data: properties = [] } = useQuery(propertiesQuery);
  /* For Awaiting Revisions: the Property Manager's name, not their email. */
  const { data: users = [] } = useQuery(usersQuery);

  const activeProjects = projects.filter(p => !p.archived && !INACTIVE_STATUSES.includes(p.status));
  const pendingPlans = plans.filter(p => p.status === 'in_review');
  const today = new Date();
  const revisionRows = awaitingRevisions(plans, withPmNames(projects, users), today);
  const activeProjectIds = new Set(projects.filter(p => !p.archived).map(p => p.id));
  // Whole days: a task due today is not overdue, and still counts as upcoming.
  const overdueTasks = tasks.filter(t => t.status !== 'complete' && t.due_date && isPastDue(t.due_date, today) && activeProjectIds.has(t.project_id));
  const upcomingMilestoneTasks = tasks
    .filter(t => t.is_milestone && t.status !== 'complete' && t.due_date && !isPastDue(t.due_date, today))
    .sort((a, b) => parseDateOnly(a.due_date) - parseDateOnly(b.due_date))
    .slice(0, 5);
  const projectMap = Object.fromEntries(projects.map(p => [p.id, p]));

  // Build per-project milestone and task maps
  const milestonesByProject = {};
  milestones.forEach(m => {
    if (!milestonesByProject[m.project_id]) milestonesByProject[m.project_id] = [];
    milestonesByProject[m.project_id].push(m);
  });
  const tasksByProject = {};
  tasks.forEach(t => {
    if (!tasksByProject[t.project_id]) tasksByProject[t.project_id] = [];
    tasksByProject[t.project_id].push(t);
  });

  /* Both views ask the same question of the same two maps. */
  const percentFor = pid => calcProjectPercent(milestonesByProject[pid] || [], tasksByProject[pid] || []);

  /* The type and portfolio filters narrow the Active Projects section only.
     The stat cards above stay whole on purpose: they are the overview, and a
     headline count that quietly followed a dropdown further down the page
     would be a number nobody could trust at a glance. */
  const shownProjects = filterProjectsByPortfolio(
    filterProjectsByType(activeProjects, typeFilter),
    portfolioByPropertyName(properties),
    portfolioFilter,
  );
  const filterWords = [
    portfolioFilter !== PORTFOLIO_FILTER_ALL ? portfolioLabel(portfolioFilter) : null,
    typeFilter !== TYPE_FILTER_ALL ? TYPE_LABELS[typeFilter] : null,
  ].filter(Boolean).join(' and ');

  return (
    <div className="space-y-8 max-w-7xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Overview of all projects and approvals</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Active Projects" value={activeProjects.length} icon={FolderKanban} href="/Projects" />
        <StatCard label="Pending Reviews" value={pendingPlans.length} icon={FileCheck2} color="text-amber-600" href="#pending-reviews" />
        <StatCard label="Awaiting Revisions" value={revisionRows.length} icon={RotateCcw} color="text-orange-600" href="#awaiting-revisions" />
        <StatCard label="Total Tasks" value={tasks.length} icon={Clock} />
        <StatCard label="Overdue Tasks" value={overdueTasks.length} icon={AlertTriangle} color="text-red-500" href="#overdue-tasks" />
      </div>

      {/* Active projects, as tiles or as a list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Active Projects</h2>
          {activeProjects.length > 0 && (
            <div className="flex items-center gap-2">
              <PortfolioFilter value={portfolioFilter} onChange={setPortfolioFilter} className="w-36 h-8 text-xs" />
              <ProjectTypeFilter value={typeFilter} onChange={setTypeFilter} className="w-36 h-8 text-xs" />
              <ProjectViewToggle value={viewMode} onChange={setViewMode} />
            </div>
          )}
        </div>
        {activeProjects.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active projects. Create one to get started.</p>
        ) : shownProjects.length === 0 ? (
          /* Distinct from the message above: there is work here, just none of
             these filters. Saying "create one" would be advice for the wrong
             problem - the fix is to change the dropdowns back. */
          <p className="text-sm text-muted-foreground">
            No active projects match {filterWords}. {activeProjects.length} active project{activeProjects.length !== 1 ? 's' : ''} with no filter.
          </p>
        ) : viewMode === VIEW_LIST ? (
          /* The list shows every active project - a row costs a line, so the
             six-tile cap that keeps the grid from swallowing the dashboard
             would only be hiding work here. ProjectList caps the height at ten
             rows and scrolls the rest. */
          <ProjectList projects={shownProjects} percentFor={percentFor} changeStatus />
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {shownProjects.slice(0, 6).map(p => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  percentComplete={percentFor(p.id)}
                  changeStatus
                />
              ))}
            </div>
            {shownProjects.length > 6 && (
              <p className="text-xs text-muted-foreground mt-3">
                Showing 6 of {shownProjects.length}. Switch to list view to see them all, or open{' '}
                <Link to="/Projects" className="underline hover:text-foreground">Projects</Link>.
              </p>
            )}
          </>
        )}
      </div>

      {/* Pending Reviews */}
      {pendingPlans.length > 0 && (
        <div id="pending-reviews">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <FileCheck2 className="w-4 h-4 text-amber-600" /> Pending Reviews
          </h2>
          <div className="space-y-2">
            {pendingPlans.map(plan => {
              const proj = projectMap[plan.project_id];
              return (
                <Link key={plan.id} to={`/PlanReview?id=${plan.id}`}>
                  <div className="flex items-center justify-between bg-card border rounded-lg px-4 py-2.5 text-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                    <div>
                      <span className="font-medium">{plan.plan_set_name}</span>
                      {proj && <span className="text-muted-foreground text-xs ml-2">· {proj.name}</span>}
                    </div>
                    <span className="text-xs text-amber-600 font-medium">In Review</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Awaiting Revisions: sent back, not yet resubmitted, longest first */}
      {revisionRows.length > 0 && (
        <div id="awaiting-revisions">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <RotateCcw className="w-4 h-4 text-orange-600" /> Awaiting Revisions
          </h2>
          <div className="space-y-2">
            {revisionRows.map(row => (
              <Link key={row.plan.id} to={`/PlanReview?id=${row.plan.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 bg-card border border-orange-100 rounded-lg px-4 py-2.5 text-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                  <div className="min-w-0">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mr-2">{row.kind}</span>
                    <span className="font-medium">{row.plan.plan_set_name}</span>
                    <span className="text-muted-foreground text-xs ml-2">· {row.project.name}</span>
                    <span className="text-muted-foreground text-xs ml-2">· PM: {row.propertyManager || 'None named'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-orange-600 font-medium">{waitingLabel(row.daysWaiting)}</span>
                    {row.sent ? (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        Sent to tenant{row.sent.timestamp ? ` ${format(new Date(row.sent.timestamp), 'MMM d')}` : ''}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Not yet sent to tenant</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Overdue Tasks */}
      {overdueTasks.length > 0 && (
        <div id="overdue-tasks">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-red-500" /> Overdue Tasks
          </h2>
          <div className="space-y-2">
            {overdueTasks.slice(0, 10).map(t => {
              const proj = projectMap[t.project_id];
              return (
                <Link key={t.id} to={`/ProjectDetail?id=${t.project_id}&tab=tasks`}>
                  <div className="flex items-center justify-between bg-card border border-red-100 rounded-lg px-4 py-2.5 text-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                    <div>
                      <span className="font-medium">{t.name}</span>
                      {proj && <span className="text-muted-foreground text-xs ml-2">· {proj.name}</span>}
                    </div>
                    <span className="text-xs text-red-500 font-medium">Due {format(parseDateOnly(t.due_date), 'MMM d')}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming Milestone Tasks */}
      {upcomingMilestoneTasks.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <Star className="w-4 h-4 text-amber-500 fill-amber-400" /> Upcoming Key Milestones
          </h2>
          <div className="space-y-2">
            {upcomingMilestoneTasks.map(t => {
              const proj = projectMap[t.project_id];
              return (
                <Link key={t.id} to={`/ProjectDetail?id=${t.project_id}&tab=tasks`}>
                  <div className="flex items-center justify-between bg-card border rounded-lg px-4 py-2.5 text-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                    <div>
                      <span className="font-medium">{t.name}</span>
                      {proj && <span className="text-muted-foreground text-xs ml-2">· {proj.name}</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">{format(parseDateOnly(t.due_date), 'MMM d, yyyy')}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity */}
      <ActivityFeed activities={activities} />
    </div>
  );
}